// routes/luxotticaRoutes.js

import { LuxotticaLayout } from '../pages/luxottica';
import Dashboard from '../pages/luxottica-dashboard';
import UserList from '../pages/manuela-users';
import UploadOriginal from '../pages/luxottica-images-upload-original';
import UploadResized from '../pages/luxottica-images-upload-resized';
import UploadLensRemoved from '../pages/luxottica-images-upload-lens-removed';
import ImageReviewOriginal from '../pages/luxottica-images-view-original';
import ImageReviewResized from '../pages/luxottica-images-view-resized';
import ImageReviewLensRemoved from '../pages/luxottica-images-view-lens-removed';
import ProtectedRoute from '../components/auth/ProtectedRoute';
import ReviewPage from '@/pages/luxottica-review';
import AdminReviewManager from '@/pages/luxottica-review-admin';
import MaskAnnotatorDemo from '@/pages/luxottica-review-LensesMask';
import CSVColumnMapper from '@/pages/luxottica-data-ingest';
import { Navigate } from 'react-router-dom';
import ReviewManagerFinal from '@/pages/luxottica-review-FinalHumanReview';
import { buildPath, buildRedirectPaths } from '../utils/routeBuilder';
import LuxotticaSystemStatus from '@/pages/luxottica-system-status';
import WorkflowManager from '@/pages/luxottica-workflows';
import GlassesFramesTable from '@/pages/luxottica-GlassesFrames-list';
import { Glasses } from 'lucide-react';
import DynamicNodeSystem from '@/pages/luxottica-sample-node-manager';
import TemplateEditor from '@/pages/luxottica-sample-node-manager-view';
import SegmentationVerificationTable from '@/pages/luxottica-segmentation-verification';
import WorkflowAnalyticsDashboard from '@/pages/luxottica-workflow-analytics';
import UserWorkActivityDashboard from '@/pages/luxottica-user-analytics';
import LensRecVerificationTable from '@/pages/luxottica-lensrec-verification';
import WorkRecordsTable from '@/pages/luxottica-work-records';
import RevisionNodesPage from '@/pages/luxottica-revision';
import RevisionGlassesFramePage from '@/pages/luxottica-revision-glasses-frames';
import WorkBatchesList from '@/pages/luxottica-work-batches';

// Redirect configuration constants
const LUXOTTICA_REDIRECT_PATHS = buildRedirectPaths('/luxottica');

const luxotticaRoutes = [
  // Root redirect to dashboard
  { 
    path: buildPath('/luxottica'), 
    element: <Navigate to={buildPath('/luxottica/dashboard')} replace />
  },
  // Dashboard
  { 
    path: buildPath('/luxottica/dashboard'), 
    element: (
      <ProtectedRoute 
        projectId="luxottica" 
        requireProjectAccess={true}
        requiredRoles={['admin', 'manager', 'reviewer']}
        loginPath={LUXOTTICA_REDIRECT_PATHS.LOGIN}
        unauthorizedPath={LUXOTTICA_REDIRECT_PATHS.UNAUTHORIZED}
        projectAccessDeniedPath={LUXOTTICA_REDIRECT_PATHS.PROJECT_ACCESS_DENIED}
      >
        <LuxotticaLayout>
          <Dashboard />
        </LuxotticaLayout>
      </ProtectedRoute>
    )
  },
  // Overview
  { 
    path: buildPath('/luxottica/overview'), 
    element: (
      <ProtectedRoute 
        projectId="luxottica" 
        requireProjectAccess={true}
        requiredRole="admin"
        loginPath={LUXOTTICA_REDIRECT_PATHS.LOGIN}
        unauthorizedPath={LUXOTTICA_REDIRECT_PATHS.UNAUTHORIZED}
        projectAccessDeniedPath={LUXOTTICA_REDIRECT_PATHS.PROJECT_ACCESS_DENIED}
      >
        <LuxotticaLayout>
          <UserList />
        </LuxotticaLayout>
      </ProtectedRoute>
    )
  },
  // Images - Upload Original
  { 
    path: buildPath('/luxottica/images/upload/original'), 
    element: (
      <ProtectedRoute 
        projectId="luxottica" 
        requireProjectAccess={true}
        requiredRole="admin"
        loginPath={LUXOTTICA_REDIRECT_PATHS.LOGIN}
        unauthorizedPath={LUXOTTICA_REDIRECT_PATHS.UNAUTHORIZED}
        projectAccessDeniedPath={LUXOTTICA_REDIRECT_PATHS.PROJECT_ACCESS_DENIED}
      >
        <LuxotticaLayout>
          <UploadOriginal />
        </LuxotticaLayout>
      </ProtectedRoute>
    )
  },
  // Images - Upload Resized
  { 
    path: buildPath('/luxottica/images/upload/resized'), 
    element: (
      <ProtectedRoute 
        projectId="luxottica" 
        requireProjectAccess={true}
        requiredRole="admin"
        loginPath={LUXOTTICA_REDIRECT_PATHS.LOGIN}
        unauthorizedPath={LUXOTTICA_REDIRECT_PATHS.UNAUTHORIZED}
        projectAccessDeniedPath={LUXOTTICA_REDIRECT_PATHS.PROJECT_ACCESS_DENIED}
      >
        <LuxotticaLayout>
          <UploadResized />
        </LuxotticaLayout>
      </ProtectedRoute>
    )
  },
  // Images - Upload Lens Removed
  { 
    path: buildPath('/luxottica/images/upload/lens-removed'), 
    element: (
      <ProtectedRoute 
        projectId="luxottica" 
        requireProjectAccess={true}
        requiredRole="admin"
        loginPath={LUXOTTICA_REDIRECT_PATHS.LOGIN}
        unauthorizedPath={LUXOTTICA_REDIRECT_PATHS.UNAUTHORIZED}
        projectAccessDeniedPath={LUXOTTICA_REDIRECT_PATHS.PROJECT_ACCESS_DENIED}
      >
        <LuxotticaLayout>
          <UploadLensRemoved />
        </LuxotticaLayout>
      </ProtectedRoute>
    )
  },
  // Images - View Original
  { 
    path: buildPath('/luxottica/images/original'), 
    element: (
      <ProtectedRoute 
        projectId="luxottica" 
        requireProjectAccess={true}
        requiredRole="admin"
        loginPath={LUXOTTICA_REDIRECT_PATHS.LOGIN}
        unauthorizedPath={LUXOTTICA_REDIRECT_PATHS.UNAUTHORIZED}
        projectAccessDeniedPath={LUXOTTICA_REDIRECT_PATHS.PROJECT_ACCESS_DENIED}
      >
        <LuxotticaLayout>
          <ImageReviewOriginal />
        </LuxotticaLayout>
      </ProtectedRoute>
    )
  },
  // Images - View Resized
  { 
    path: buildPath('/luxottica/images/view/resized'), 
    element: (
      <ProtectedRoute 
        projectId="luxottica" 
        requireProjectAccess={true}
        requiredRole="admin"
        loginPath={LUXOTTICA_REDIRECT_PATHS.LOGIN}
        unauthorizedPath={LUXOTTICA_REDIRECT_PATHS.UNAUTHORIZED}
        projectAccessDeniedPath={LUXOTTICA_REDIRECT_PATHS.PROJECT_ACCESS_DENIED}
      >
        <LuxotticaLayout>
          <ImageReviewResized />
        </LuxotticaLayout>
      </ProtectedRoute>
    )
  },
  // Images - View Lens Removed
  { 
    path: buildPath('/luxottica/images/view/lens-removed'), 
    element: (
      <ProtectedRoute 
        projectId="luxottica" 
        requireProjectAccess={true}
        requiredRole="admin"
        loginPath={LUXOTTICA_REDIRECT_PATHS.LOGIN}
        unauthorizedPath={LUXOTTICA_REDIRECT_PATHS.UNAUTHORIZED}
        projectAccessDeniedPath={LUXOTTICA_REDIRECT_PATHS.PROJECT_ACCESS_DENIED}
      >
        <LuxotticaLayout>
          <ImageReviewLensRemoved />
        </LuxotticaLayout>
      </ProtectedRoute>
    )
  },
  // Data Ingest
  { 
    path: buildPath('/luxottica/data/ingest'), 
    element: (
      <ProtectedRoute 
        projectId="luxottica" 
        requireProjectAccess={true}
        requiredRoles={['admin', 'viewer', 'manager', 'user', 'client']}
        loginPath={LUXOTTICA_REDIRECT_PATHS.LOGIN}
        unauthorizedPath={LUXOTTICA_REDIRECT_PATHS.UNAUTHORIZED}
        projectAccessDeniedPath={LUXOTTICA_REDIRECT_PATHS.PROJECT_ACCESS_DENIED}
      >
        <LuxotticaLayout>
          <CSVColumnMapper />
        </LuxotticaLayout>
      </ProtectedRoute>
    )
  },
  // Review - Final
  { 
    path: buildPath('/luxottica/review/final'), 
    element: (
      <ProtectedRoute 
        projectId="luxottica" 
        requireProjectAccess={true}
        requiredRoles={['admin', 'manager', 'reviewer']}
        loginPath={LUXOTTICA_REDIRECT_PATHS.LOGIN}
        unauthorizedPath={LUXOTTICA_REDIRECT_PATHS.UNAUTHORIZED}
        projectAccessDeniedPath={LUXOTTICA_REDIRECT_PATHS.PROJECT_ACCESS_DENIED}
      >
        <LuxotticaLayout>
          <ReviewManagerFinal />
        </LuxotticaLayout>
      </ProtectedRoute>
    )
  },
  // Review - Mask Demo
  { 
    path: buildPath('/luxottica/review/mask/demo'), 
    element: (
      <ProtectedRoute 
        projectId="luxottica" 
        requireProjectAccess={true}
        requiredRole={['admin']}
        loginPath={LUXOTTICA_REDIRECT_PATHS.LOGIN}
        unauthorizedPath={LUXOTTICA_REDIRECT_PATHS.UNAUTHORIZED}
        projectAccessDeniedPath={LUXOTTICA_REDIRECT_PATHS.PROJECT_ACCESS_DENIED}
      >
        <LuxotticaLayout>
          <MaskAnnotatorDemo />
        </LuxotticaLayout>
      </ProtectedRoute>
    )
  },
  // Review - Admin
  { 
    path: buildPath('/luxottica/review/admin'), 
    element: (
      <ProtectedRoute 
        projectId="luxottica" 
        requireProjectAccess={true}
        requiredRole={['admin']}
        loginPath={LUXOTTICA_REDIRECT_PATHS.LOGIN}
        unauthorizedPath={LUXOTTICA_REDIRECT_PATHS.UNAUTHORIZED}
        projectAccessDeniedPath={LUXOTTICA_REDIRECT_PATHS.PROJECT_ACCESS_DENIED}
      >
        <LuxotticaLayout>
          <AdminReviewManager />
        </LuxotticaLayout>
      </ProtectedRoute>
    )
  },
  // Review - Admin
  { 
    path: buildPath('/luxottica/system-status'), 
    element: (
      <ProtectedRoute 
        projectId="luxottica" 
        requireProjectAccess={true}
        requiredRoles={['admin', 'manager', 'reviewer']}
        loginPath={LUXOTTICA_REDIRECT_PATHS.LOGIN}
        unauthorizedPath={LUXOTTICA_REDIRECT_PATHS.UNAUTHORIZED}
        projectAccessDeniedPath={LUXOTTICA_REDIRECT_PATHS.PROJECT_ACCESS_DENIED}
      >
        <LuxotticaLayout>
          <LuxotticaSystemStatus />
        </LuxotticaLayout>
      </ProtectedRoute>
    )
  },
  { 
    path: buildPath('/luxottica/workflows-template'), 
    element: (
      <ProtectedRoute 
        projectId="luxottica" 
        requireProjectAccess={true}
        requiredRole="admin"
        loginPath={LUXOTTICA_REDIRECT_PATHS.LOGIN}
        unauthorizedPath={LUXOTTICA_REDIRECT_PATHS.UNAUTHORIZED}
        projectAccessDeniedPath={LUXOTTICA_REDIRECT_PATHS.PROJECT_ACCESS_DENIED}
      >
        <LuxotticaLayout>
          <WorkflowManager />
        </LuxotticaLayout>
      </ProtectedRoute>
    )
  },
  { 
    path: buildPath('/luxottica/workflows-analytics'), 
    element: (
      <ProtectedRoute 
        projectId="luxottica" 
        requireProjectAccess={true}
        requiredRoles={['admin', 'manager']}
        loginPath={LUXOTTICA_REDIRECT_PATHS.LOGIN}
        unauthorizedPath={LUXOTTICA_REDIRECT_PATHS.UNAUTHORIZED}
        projectAccessDeniedPath={LUXOTTICA_REDIRECT_PATHS.PROJECT_ACCESS_DENIED}
      >
        <LuxotticaLayout>
          <WorkflowAnalyticsDashboard />
        </LuxotticaLayout>
      </ProtectedRoute>
    )
  },
  { 
    path: buildPath('/luxottica/glasses'), 
    element: (
      <ProtectedRoute 
        projectId="luxottica" 
        requireProjectAccess={true}
        requiredRoles={['admin', 'manager']}
        loginPath={LUXOTTICA_REDIRECT_PATHS.LOGIN}
        unauthorizedPath={LUXOTTICA_REDIRECT_PATHS.UNAUTHORIZED}
        projectAccessDeniedPath={LUXOTTICA_REDIRECT_PATHS.PROJECT_ACCESS_DENIED}
      >
        <LuxotticaLayout>
          <GlassesFramesTable />
        </LuxotticaLayout>
      </ProtectedRoute>
    )
  },
  { 
    path: buildPath('/luxottica/nodes/test'), 
    element: (
      <ProtectedRoute 
        projectId="luxottica" 
        requireProjectAccess={true}
        requiredRole="admin"
        loginPath={LUXOTTICA_REDIRECT_PATHS.LOGIN}
        unauthorizedPath={LUXOTTICA_REDIRECT_PATHS.UNAUTHORIZED}
        projectAccessDeniedPath={LUXOTTICA_REDIRECT_PATHS.PROJECT_ACCESS_DENIED}
      >
        <LuxotticaLayout>
          <DynamicNodeSystem />
        </LuxotticaLayout>
      </ProtectedRoute>
    )
  },
  { 
    path: buildPath('/luxottica/nodes/view'), 
    element: (
      <ProtectedRoute 
        projectId="luxottica" 
        requireProjectAccess={true}
        requiredRole="admin"
        loginPath={LUXOTTICA_REDIRECT_PATHS.LOGIN}
        unauthorizedPath={LUXOTTICA_REDIRECT_PATHS.UNAUTHORIZED}
        projectAccessDeniedPath={LUXOTTICA_REDIRECT_PATHS.PROJECT_ACCESS_DENIED}
      >
        <LuxotticaLayout>
          <TemplateEditor />
        </LuxotticaLayout>
      </ProtectedRoute>
    )
  },
  { 
    path: buildPath('/luxottica/segmentation'), 
    element: (
      <ProtectedRoute 
        projectId="luxottica" 
        requireProjectAccess={true}
        requiredRoles={['admin', 'manager']}
        loginPath={LUXOTTICA_REDIRECT_PATHS.LOGIN}
        unauthorizedPath={LUXOTTICA_REDIRECT_PATHS.UNAUTHORIZED}
        projectAccessDeniedPath={LUXOTTICA_REDIRECT_PATHS.PROJECT_ACCESS_DENIED}
      >
        <LuxotticaLayout>
          <SegmentationVerificationTable />
        </LuxotticaLayout>
      </ProtectedRoute>
    )
  },
  { 
    path: buildPath('/luxottica/user'), 
    element: (
      <ProtectedRoute 
        projectId="luxottica" 
        requireProjectAccess={true}
        requiredRoles={['admin', 'manager', 'reviewer', 'user']}
        loginPath={LUXOTTICA_REDIRECT_PATHS.LOGIN}
        unauthorizedPath={LUXOTTICA_REDIRECT_PATHS.UNAUTHORIZED}
        projectAccessDeniedPath={LUXOTTICA_REDIRECT_PATHS.PROJECT_ACCESS_DENIED}
      >
        <LuxotticaLayout>
          <UserWorkActivityDashboard />
        </LuxotticaLayout>
      </ProtectedRoute>
    )
  },
  { 
    path: buildPath('/luxottica/lensrec'), 
    element: (
      <ProtectedRoute 
        projectId="luxottica" 
        requireProjectAccess={true}
        requiredRoles={['admin']}
        loginPath={LUXOTTICA_REDIRECT_PATHS.LOGIN}
        unauthorizedPath={LUXOTTICA_REDIRECT_PATHS.UNAUTHORIZED}
        projectAccessDeniedPath={LUXOTTICA_REDIRECT_PATHS.PROJECT_ACCESS_DENIED}
      >
        <LuxotticaLayout>
          <LensRecVerificationTable />
        </LuxotticaLayout>
      </ProtectedRoute>
    )
  },
  { 
    path: buildPath('/luxottica/work-record'), 
    element: (
      <ProtectedRoute 
        projectId="luxottica" 
        requireProjectAccess={true}
        requiredRoles={['admin']}
        loginPath={LUXOTTICA_REDIRECT_PATHS.LOGIN}
        unauthorizedPath={LUXOTTICA_REDIRECT_PATHS.UNAUTHORIZED}
        projectAccessDeniedPath={LUXOTTICA_REDIRECT_PATHS.PROJECT_ACCESS_DENIED}
      >
        <LuxotticaLayout>
          <WorkRecordsTable />
        </LuxotticaLayout>
      </ProtectedRoute>
    )
  },
  { 
    path: buildPath('/luxottica/work-batches'), 
    element: (
      <ProtectedRoute 
        projectId="luxottica" 
        requireProjectAccess={true}
        requiredRoles={['admin']}
        loginPath={LUXOTTICA_REDIRECT_PATHS.LOGIN}
        unauthorizedPath={LUXOTTICA_REDIRECT_PATHS.UNAUTHORIZED}
        projectAccessDeniedPath={LUXOTTICA_REDIRECT_PATHS.PROJECT_ACCESS_DENIED}
      >
        <LuxotticaLayout>
          <WorkBatchesList />
        </LuxotticaLayout>
      </ProtectedRoute>
    )
  },
  { 
    path: buildPath('/luxottica/revision'), 
    element: (
      <ProtectedRoute 
        projectId="luxottica" 
        requireProjectAccess={true}
        requiredRoles={['admin']}
        loginPath={LUXOTTICA_REDIRECT_PATHS.LOGIN}
        unauthorizedPath={LUXOTTICA_REDIRECT_PATHS.UNAUTHORIZED}
        projectAccessDeniedPath={LUXOTTICA_REDIRECT_PATHS.PROJECT_ACCESS_DENIED}
      >
        <LuxotticaLayout>
          <RevisionNodesPage />
        </LuxotticaLayout>
      </ProtectedRoute>
    )
  },
  { 
    path: buildPath('/luxottica/revision/glasses-frames'), 
    element: (
      <ProtectedRoute 
        projectId="luxottica" 
        requireProjectAccess={true}
        requiredRoles={['admin']}
        loginPath={LUXOTTICA_REDIRECT_PATHS.LOGIN}
        unauthorizedPath={LUXOTTICA_REDIRECT_PATHS.UNAUTHORIZED}
        projectAccessDeniedPath={LUXOTTICA_REDIRECT_PATHS.PROJECT_ACCESS_DENIED}
      >
        <LuxotticaLayout>
          <RevisionGlassesFramePage />
        </LuxotticaLayout>
      </ProtectedRoute>
    )
  }
];

export default luxotticaRoutes;